import {AfterViewInit, Component, ElementRef, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {TooltipService} from '@irb-common/components/tooltip/tooltip.service';
import {FormControl, Validators} from '@angular/forms';
import {conditionalValidator} from '@irb-common/validators/conditionalValidator';


@Component({ standalone: false,
    selector: 'irb-required-textbox-editor',
    template: `
        <input pInputText #inputField [(ngModel)]="value"
               (input)="onInputChange()"
               tooltip
               [formControl]="inputControl"
               [tooltipTitle]="requiredTextboxTooltipMessage"
               [tooltipPosition]="'right'"
               [style.width]="width"
               tooltipClass="error"
               [tooltipHorizontalOffset]="10"
               (keydown)="preventCharInput($event)"
               (paste)="preventCharInput($event)"
               (drop)="preventCharInput($event)"
        >
    `
})
export class RequiredTextboxEditor implements ICellEditorAngularComp, AfterViewInit {
    private params: any = {};
    value: string;
    tooltiptext: string;
    autofocus: boolean;
    autoselect: boolean;
    minLength: number;
    maxLength: number;
    minLenWord: number;
    maxLenWord: number;
    checkForSpecialChars: boolean;
    requiredTextboxTooltipMessage: string = '';
    width: any = '80px';
    private originalValue: string;

    @ViewChild("inputField") inputField: ElementRef;

    inputControl: FormControl;

    constructor(private tts: TooltipService) {
    }

    agInit(params: any): void {
        this.params = params;
        this.originalValue = this.params.value;
        this.tooltiptext = params.tooltiptext ? params.tooltiptext : 'Please enter a valid value';
        this.autofocus = params.autofocus || false;
        this.autoselect = params.autoselect || false;
        this.checkForSpecialChars = params.checkForSpecialChars || false;
        this.minLength = params.minLength;
        this.maxLength = params.maxLength;
        this.minLenWord = params.minLenWord;
        this.maxLenWord = params.maxLenWord;
        this.width = params.width || '80px';
        this.value = params.value ? params.value.toString() : '';

        const v: any = [Validators.required];
        if (this.minLength) {
            v.push(Validators.minLength(this.minLength));
        }
        if (this.maxLength) {
            v.push(Validators.maxLength(this.maxLength));
        }
        if (this.minLenWord) {
            v.push(conditionalValidator(() => this.minLength, (c: FormControl) => {
                if (c.value) {
                    const arrWords = c.value.trim().split(" ");
                    let invalid: boolean = false;
                    for (let i = 0; i < arrWords.length; i++) {
                        if (arrWords[i].length < this.minLenWord) {
                            invalid = true;
                            break;
                        }
                    }

                    const r = invalid ? {'violatesMinConstraint': {value: c.value}} : null;
                    return r;
                }

                return null;
            }));
        }
        if (this.maxLenWord) {
            v.push(conditionalValidator(() => this.minLength, (c: FormControl) => {
                if (c.value) {
                    const arrWords = c.value.trim().split(" ");
                    let invalid: boolean = false;
                    for (let i = 0; i < arrWords.length; i++) {
                        if (arrWords[i].length > this.maxLenWord) {
                            invalid = true;
                            break;
                        }
                    }

                    const r = invalid ? {'violatesMaxConstraint': {value: c.value}} : null;
                    return r;
                }

                return null;
            }));
        }

        this.inputControl = new FormControl(this.value, v);
    }

    getValue(): any {
        if (!this.inputControl.valid) {
            this.value = this.originalValue;
        }
        return this.value;
    }

    ngAfterViewInit(): void {
        if (this.autofocus || this.autoselect) {
            window.setTimeout(() => {
                if (this.autofocus) {
                    this.inputField.nativeElement.focus();
                }
                if (this.autoselect) {
                    this.inputField.nativeElement.select();
                }
                this.onInputChange();
            }, 200);
        }
    }

    onInputChange() {

        if (!this.inputControl.valid && this.inputControl.value !== null) {
            this.requiredTextboxTooltipMessage = this.tooltiptext;
        } else {
            this.requiredTextboxTooltipMessage = null;
            this.tts.remove();
        }
        this.value = this.inputControl.value;
    }

    preventCharInput(event: any) {
        if (this.checkForSpecialChars) {
            if (event.type === "keydown") {
                if (!this.hasInvalidChars(event.key)) {
                    event.preventDefault();
                } else {
                    this.requiredTextboxTooltipMessage = '';
                }
            }
            if (event.type === "paste" || event.type === "drop") {
                var textContent = (event.type === "paste" ? event.clipboardData : event.dataTransfer).getData('text');

                for (var i = 0; i < textContent.length; i++) {
                    if (!this.hasInvalidChars(textContent[i])) {
                        event.preventDefault();
                    }
                }
                this.requiredTextboxTooltipMessage = '';
            }
        }
    }

    private hasInvalidChars(s: string) {
        return !(/[~`!@#$%\^&*,.+=_[\]\\';/{}()|\\":<>-\?]/g.test(s));
    }
}
